import React, { useContext, useState } from "react";

import styled from "@emotion/styled";

import pushToAnalytics from "../../../../../utils/analytics";
import StorageHandler from "../../../../../utils/StorageHandler";
import NewInput from "../../../../shared/forms/NewInput";
import Button from "../../../../shared/NewForms/Button";
import { PageContext } from "../../../../shared/Page";
import useSnackbar from "../../../../shared/Snackbar/store";
import useCalculatorStore from "../store";
import AmountInput from "./AmountInput";
import ZipcodeInput from "./ZipcodeInput";

const storage = new StorageHandler();

const CalculatorContainer = styled.div`
    display: flex;
    justify-content: flex-start;
`;

const Form = styled.form`
    display: flex;
    flex-flow: row wrap;
    justify-content: flex-start;
    width: 100%;
    margin-bottom: 20px;
    gap: 6px 12px;

    ${({ theme }) => theme.breakpoints.from.small.css`
        justify-content: space-between;
    `}
`;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: 20px;
    margin-bottom: 30px;
`;

const InputWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 48%;

    ${({ theme }) => theme.breakpoints.from.small.css`
        width: 30%;
    `}
`;

const Calculator = ({ isLoading = false }) => {
    const [
        zipcode,
        amount,
        up,
        checkingForZipcode,
        zipcodeIsValid,
        amountIsValid,
        setZipcode,
        setAmount,
        setUp,
        calculate,
        setResult,
        setAdditions,
    ] = useCalculatorStore((state) => [
        state.zipcode,
        state.amount,
        state.up,
        state.checkingForZipcode,
        state.zipcodeIsValid,
        state.amountIsValid,
        state.setZipcode,
        state.setAmount,
        state.setUp,
        state.calculate,
        state.setResult,
        state.setAdditions,
    ]);

    const pageContext = useContext(PageContext);
    const { updateLastCalculator } = pageContext.actions;
    const { notify } = useSnackbar();

    const [localZipcode, setLocalZipcode] = useState(zipcode);
    const [localAmount, setLocalAmount] = useState(amount);
    const [localUp, setLocalUp] = useState(up);
    const [mayCalculate, setMayCalculate] = useState(false);
    const [zipcodeError, setZipcodeError] = useState(false);
    const [amountError, setAmountError] = useState(false);
    const [upError, setUpError] = useState(false);

    const onChangeAmount = (event) => {
        setLocalAmount(event.target.value);
        setMayCalculate(true);
    };

    const onChangeUp = (event) => {
        setLocalUp(event.target.value);
        setMayCalculate(true);
    };

    const onSubmit = (event) => {
        event.preventDefault();
        if (!localZipcode) {
            setZipcodeError(true);
            return;
        }

        if (localZipcode.length === 5) {
            storage.set("hel:last-zipcode-input-value", localZipcode);
        }

        if (!localAmount) {
            setAmountError(true);
            return false;
        }
        if (localUp === "") {
            setUpError(true);
            return;
        }

        if (
            !zipcodeError &&
            !amountError &&
            !upError &&
            zipcodeIsValid &&
            amountIsValid
        ) {
            setZipcode(localZipcode);
            setAmount(localAmount);
            setUp(localUp === "" ? 1 : localUp);
            setMayCalculate(false);
            updateLastCalculator("heizoel");
            calculate(localZipcode, localAmount, localUp, notify);
            // Trigger a gtm event
            pushToAnalytics("hel:calculate", {
                zipcode: localZipcode,
                amount: localAmount,
                up: localUp === "" ? 1 : localUp,
            });
        } else {
            setResult([]);
            setAdditions(null);
        }
    };

    if (isLoading) {
        return null;
    }

    return (
        <CalculatorContainer>
            <Form onSubmit={onSubmit}>
                <InputWrapper>
                    <ZipcodeInput
                        setMayCalculate={setMayCalculate}
                        setZipcode={setLocalZipcode}
                        localZipcode={localZipcode}
                        zipcodeError={zipcodeError}
                        setZipcodeError={setZipcodeError}
                    />
                </InputWrapper>
                <InputWrapper>
                    <AmountInput
                        localAmount={localAmount}
                        onChangeAmount={onChangeAmount}
                        setAmountError={setAmountError}
                        zipcodeError={zipcodeError}
                        localZipcode={localZipcode}
                        amountError={amountError}
                    />
                </InputWrapper>
                <InputWrapper>
                    <NewInput
                        name="unloading_points"
                        placeholder="Haushalte"
                        value={localUp}
                        onChange={onChangeUp}
                        validation="hel:up"
                        onError={() => setUpError(true)}
                        onErrorSolved={() => setUpError(false)}
                        forceError={upError}
                        required
                    />
                </InputWrapper>
                <ButtonWrapper>
                    <Button
                        type="submit"
                        data-gtag="hel-calculate-advanced"
                        disabled={checkingForZipcode}
                        color={mayCalculate ? "red" : "redOutline"}
                    >
                        Neu berechnen
                    </Button>
                </ButtonWrapper>
            </Form>
        </CalculatorContainer>
    );
};

export default Calculator;
